/**
 * LeadDetailModal - Shows full lead profile when clicking "Ver dados" in AdminEventsTab.
 */

import React, { useState, useEffect } from 'react';
import { getLeadDetail, type LeadDetail } from '@/services/adminDashboardService';
import {
  X,
  User,
  CreditCard,
  ClipboardList,
  Target,
  Activity,
  Clock,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface LeadDetailModalProps {
  userId: string | null;
  eventMetadata?: Record<string, any> | null;
  onClose: () => void;
}

const EVENT_LABELS: Record<string, string> = {
  account_created: 'Conta criada',
  login_completed: 'Login',
  onboarding_started: 'Onboarding iniciado',
  onboarding_completed: 'Onboarding completo',
  carousel_created: 'Carrossel criado',
  carousel_edited: 'Carrossel editado',
  carousel_exported: 'Carrossel exportado',
  checkout_initiated: 'Checkout iniciado',
  checkout_form_started: 'Form iniciado',
  checkout_form_completed: 'Form completo',
  payment_approved: 'Pagamento aprovado',
  payment_failed: 'Pagamento falhou',
  refund_requested: 'Reembolso',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  active: 'bg-green-100 text-green-800',
  TRIALING: 'bg-yellow-100 text-yellow-800',
  trialing: 'bg-yellow-100 text-yellow-800',
  CANCELED: 'bg-red-100 text-red-800',
  canceled: 'bg-red-100 text-red-800',
  PAST_DUE: 'bg-orange-100 text-orange-800',
  past_due: 'bg-orange-100 text-orange-800',
  PENDING: 'bg-gray-100 text-gray-800',
  pending: 'bg-gray-100 text-gray-800',
};

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Badge({ label, colorClass }: { label: string; colorClass?: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass || 'bg-muted text-muted-foreground'}`}>
      {label}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium text-right max-w-[60%] break-all">{value}</span>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      </div>
      {children}
    </div>
  );
}

export default function LeadDetailModal({ userId, eventMetadata, onClose }: LeadDetailModalProps) {
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLead(null);
      return;
    }

    setLoading(true);
    setError(null);
    getLeadDetail(userId)
      .then((data) => setLead(data))
      .catch(() => setError('Erro ao carregar dados do lead'))
      .finally(() => setLoading(false));
  }, [userId]);

  if (!userId) return null;

  const initials = lead?.display_name
    ? lead.display_name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <div className="sticky top-0 bg-background z-10 flex justify-end p-3 pb-0">
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {loading && (
            <div className="py-12 text-center text-muted-foreground">Carregando...</div>
          )}

          {error && (
            <div className="py-12 text-center text-red-500">{error}</div>
          )}

          {lead && !loading && (
            <>
              {/* Header */}
              <div className="flex items-center gap-4">
                {lead.avatar_url ? (
                  <img src={lead.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                    {initials}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-foreground truncate">
                    {lead.display_name || 'Sem nome'}
                  </h3>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                    <span>{lead.email}</span>
                    {lead.phone && <span>{lead.phone}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Criado em: {formatDate(lead.created_at)}
                    {lead.handle && <span className="ml-3">@{lead.handle}</span>}
                  </div>
                </div>
              </div>

              {/* Assinatura */}
              <Section title="Assinatura" icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}>
                {lead.subscription ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-foreground font-medium">{lead.subscription.plan_name}</span>
                      <Badge
                        label={lead.subscription.status}
                        colorClass={STATUS_COLORS[lead.subscription.status]}
                      />
                      {lead.subscription.period && (
                        <Badge label={lead.subscription.period} />
                      )}
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      {lead.subscription.started_at && (
                        <span>Início: {formatDate(lead.subscription.started_at)}</span>
                      )}
                      {lead.subscription.expires_at && (
                        <span>Expira: {formatDate(lead.subscription.expires_at)}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Sem assinatura</p>
                )}
              </Section>

              {/* Onboarding */}
              <Section title="Onboarding" icon={<ClipboardList className="h-4 w-4 text-muted-foreground" />}>
                {lead.onboarding_completed ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <InfoRow label="Objetivo" value={lead.onboarding_data.objetivo_carrosseis} />
                    <InfoRow label="Conteúdo" value={lead.onboarding_data.tipo_conteudo} />
                    <InfoRow label="Leads Instagram" value={lead.onboarding_data.leads_instagram} />
                    <InfoRow label="Faturamento" value={lead.onboarding_data.faturamento} />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Não completou o onboarding</p>
                )}
              </Section>

              {/* Atribuição / UTM */}
              <Section title="Atribuição / UTM" icon={<Target className="h-4 w-4 text-muted-foreground" />}>
                <div className="space-y-1">
                  {lead.first_touch_channel && (
                    <div className="mb-2">
                      <Badge label={lead.first_touch_channel} colorClass="bg-primary/10 text-primary" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <InfoRow label="Source" value={lead.utm_source} />
                    <InfoRow label="Medium" value={lead.utm_medium} />
                    <InfoRow label="Campaign" value={lead.utm_campaign} />
                    <InfoRow label="Content" value={lead.utm_content} />
                    <InfoRow label="Term" value={lead.utm_term} />
                    <InfoRow label="Device" value={lead.device_type} />
                    <InfoRow label="Landing Page" value={lead.landing_page} />
                    <InfoRow label="Referrer" value={lead.referrer} />
                    <InfoRow label="Influencer" value={lead.influencer_id} />
                    <InfoRow label="Cupom" value={lead.coupon_code} />
                    <InfoRow label="País" value={lead.country} />
                    <InfoRow label="Idioma" value={lead.language} />
                  </div>
                  {!lead.first_touch_channel && !lead.utm_source && !lead.utm_medium && (
                    <p className="text-sm text-muted-foreground">Sem dados de atribuição</p>
                  )}
                </div>
              </Section>

              {/* Atividade */}
              <Section title="Atividade" icon={<Activity className="h-4 w-4 text-muted-foreground" />}>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">Criou carrossel:</span>
                    <Badge
                      label={lead.created_carousel ? 'Sim' : 'Não'}
                      colorClass={lead.created_carousel ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">Pagou:</span>
                    <Badge
                      label={lead.has_paid ? 'Sim' : 'Não'}
                      colorClass={lead.has_paid ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                    />
                  </div>
                </div>
                {lead.cancellation && (
                  <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg text-sm">
                    <div className="font-medium text-red-800 dark:text-red-300">
                      Cancelamento: {lead.cancellation.reason}
                    </div>
                    {lead.cancellation.feedback && (
                      <p className="text-red-700 dark:text-red-400 mt-1">{lead.cancellation.feedback}</p>
                    )}
                    <p className="text-red-600/60 dark:text-red-400/60 text-xs mt-1">
                      {formatDate(lead.cancellation.created_at)}
                    </p>
                  </div>
                )}
              </Section>

              {/* Timeline de Eventos */}
              <Section title="Timeline de Eventos" icon={<Clock className="h-4 w-4 text-muted-foreground" />}>
                {lead.events.length > 0 ? (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {lead.events.map((evt) => {
                      const hasEvtMeta = evt.metadata && Object.keys(evt.metadata).length > 0;
                      const isExpanded = expandedEventId === evt.id;
                      return (
                        <div key={evt.id} className="text-sm">
                          <div className="flex items-center gap-2 py-1">
                            <span className="text-xs text-muted-foreground whitespace-nowrap w-28 shrink-0">
                              {formatDate(evt.created_at)}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {EVENT_LABELS[evt.event_name] || evt.event_name}
                            </span>
                            {hasEvtMeta && (
                              <button
                                onClick={() => setExpandedEventId(isExpanded ? null : evt.id)}
                                className="text-xs text-muted-foreground hover:text-foreground ml-auto"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                )}
                              </button>
                            )}
                          </div>
                          {isExpanded && hasEvtMeta && (
                            <pre className="ml-[7.5rem] p-2 bg-muted rounded text-xs overflow-auto max-w-full mb-1">
                              {JSON.stringify(evt.metadata, null, 2)}
                            </pre>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum evento registrado</p>
                )}
              </Section>

              {/* Metadata do Evento Original */}
              {eventMetadata && Object.keys(eventMetadata).length > 0 && (
                <div className="border border-border rounded-lg">
                  <button
                    onClick={() => setMetadataOpen(!metadataOpen)}
                    className="w-full flex items-center gap-2 p-4 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {metadataOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    Metadata do Evento Clicado
                  </button>
                  {metadataOpen && (
                    <div className="px-4 pb-4">
                      <pre className="p-3 bg-muted rounded text-xs overflow-auto">
                        {JSON.stringify(eventMetadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
